/**
 * Site-wide SEO / AEO / GEO copy & structured-data helpers for iLeafs.
 * Keep claims wellness-oriented — no disease-cure language.
 */

export const SITE_ORIGIN = 'https://ileafs.store';

export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/indusviva/products/ipulse-studio.jpg`;

export const SEO_KEYWORDS = [
  'iLeafs',
  'iLeaf',
  'ileafs.store',
  'IndusViva',
  'wellness products India',
  'health products online',
  'Ayurvedic wellness',
  'immunity drink',
  'antioxidant superfruit',
  'healthy glucose balance',
  'blood sugar support lifestyle',
  'diabetes lifestyle wellness',
  'sugar balance coffee',
  'iCoffee',
  'iPulse',
  'iCare',
  'iCharge',
  'iGlow',
  'iSlim',
  'women wellness drink',
  'vegetarian health products',
  'verified reseller',
  'trusted guaranteed providers',
].join(', ');

export type PageSeo = {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  noindex?: boolean;
  type?: 'website' | 'product' | 'article';
  image?: string;
};

export const ROUTE_SEO: Record<string, PageSeo> = {
  '/': {
    title: 'iLeafs — Wellness & Health Products | Trusted IndusViva Store',
    description:
      'Shop official IndusViva wellness products on iLeafs: immunity, energy, healthy glucose balance, women’s wellness, skin radiance & more. Trusted & guaranteed providers. India delivery.',
    path: '/',
    keywords: SEO_KEYWORDS,
  },
  '/shop': {
    title: 'Shop Wellness & Health Products | iLeafs IndusViva Catalog',
    description:
      'Browse the live IndusViva catalog on iLeafs — iPulse, iCare, iCharge, iCoffee for sugar balance, iGlow, iSlim. Authentic health & wellness products with verified reseller assurance.',
    path: '/shop',
  },
  '/about': {
    title: 'About iLeafs — Ayurveda Meets Modern Wellness Science',
    description:
      'Learn how iLeafs and IndusViva blend Advanced Ayurveda with modern science for holistic wellness — immunity, vitality, metabolic balance, and everyday health in India.',
    path: '/about',
  },
  '/faq': {
    title: "FAQ — Orders, Shipping, Returns | iLeafs Wellness Help",
    description:
      'Answers on IndusViva orders, PayU payments, pincode delivery, Virtual Office, 30-day returns, and consumer grievance under Direct Selling Rules 2021.',
    path: '/faq',
  },
  '/terms': {
    title: 'Terms & Conditions | iLeafs',
    description: 'Terms of use for the iLeafs verified storefront and IndusViva product purchases.',
    path: '/terms',
  },
  '/contact': {
    title: 'Contact iLeafs Support | Wellness Store Help',
    description: 'Contact iLeafs for product questions, order help, and wellness catalog support. Email info@ileafs.store.',
    path: '/contact',
  },
  '/connect': {
    title: 'Get Connected | Join iLeafs Wellness Community',
    description: 'Connect with iLeafs for partnership and wellness community opportunities with trusted IndusViva products.',
    path: '/connect',
  },
  '/product': {
    title: 'Product Details | iLeafs IndusViva Wellness',
    description:
      'Official IndusViva wellness product details on iLeafs — ingredients, benefits, dosage guidance, and secure India checkout.',
    path: '/product',
    type: 'product',
  },
  '/cart': {
    title: 'Cart | iLeafs',
    description: 'Your iLeafs shopping cart.',
    path: '/cart',
    noindex: true,
  },
  '/checkout': {
    title: 'Checkout | iLeafs',
    description: 'Secure checkout for IndusViva wellness products.',
    path: '/checkout',
    noindex: true,
  },
  '/account': {
    title: 'My Account | iLeafs',
    description: 'Manage your iLeafs account and orders.',
    path: '/account',
    noindex: true,
  },
  '/account/order': {
    title: 'Order Details | iLeafs',
    description: 'View order details.',
    path: '/account/order',
    noindex: true,
  },
  '/payment/result': {
    title: 'Payment | iLeafs',
    description: 'Payment status.',
    path: '/payment/result',
    noindex: true,
  },
  '/report': {
    title: 'Report an Issue | iLeafs',
    description: 'Report a storefront or order issue to iLeafs support.',
    path: '/report',
    noindex: true,
  },
  '/404': {
    title: 'Page Not Found | iLeafs',
    description: 'The page you requested was not found on iLeafs.',
    path: '/404',
    noindex: true,
  },
};

export function absoluteUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http')) return pathOrUrl;
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'iLeafs',
    alternateName: ['iLeaf', 'iLeafs Store', 'ileafs.store'],
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/assets/img/logo.png`,
    email: 'info@ileafs.store',
    description:
      'iLeafs is a trusted & guaranteed provider marketplace for official IndusViva wellness and health products in India.',
    sameAs: [
      'https://www.instagram.com/ileafs.store',
      'https://www.facebook.com/share/1EosN9jro2/',
      'https://x.com/mhdmidhlajpp',
    ],
    areaServed: { '@type': 'Country', name: 'India' },
    brand: { '@type': 'Brand', name: 'IndusViva' },
  };
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'iLeafs',
    alternateName: ['iLeaf', 'iLeafs Wellness Store'],
    url: SITE_ORIGIN,
    description:
      'Shop IndusViva wellness products — immunity, energy, healthy glucose balance, women’s health, skin radiance.',
    inLanguage: 'en-IN',
    publisher: { '@type': 'Organization', name: 'iLeafs' },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_ORIGIN}/shop`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function storeJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    name: 'iLeafs',
    url: SITE_ORIGIN,
    image: DEFAULT_OG_IMAGE,
    priceRange: '₹₹',
    currenciesAccepted: 'INR',
    paymentAccepted: 'UPI, Credit Card, Debit Card, Net Banking',
    areaServed: 'IN',
    brand: 'IndusViva',
  };
}
